/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import {
  type ActionHandlers,
  Dispatcher,
  type DispatcherOptions,
  type Message,
} from "@utils/dispatch";
import type { WbaCtx } from "@wbaudio/core/wbactx";
import { DefaultAnalyserParams } from "./utils.tsx";
import { Rms } from "./dsp.tsx";

//#region dispatch
export interface AnalyseEvent {
  type: "analyser";
  timeDomain: Float32Array;
  freqData: Float32Array;
  byteFreq: Uint8Array;
  byteTime: Uint8Array;
  volume: number;
}

export const AnalyserFftSizes = [256, 512, 1024, 2048, 4096, 8192, 16384, 32768] as const;

export interface WbaAnalyserState {
  "analyser/enabled": boolean;
  "analyser/fps": number;
  "analyser/fftSize": number;
  "analyser/smoothing": number;
  "analyser/minDecibels": number;
  "analyser/maxDecibels": number;
}

export const WbaAnalyserDefaultState: WbaAnalyserState = {
  "analyser/enabled": true,
  "analyser/fps": 30,
  "analyser/fftSize": DefaultAnalyserParams.fftSize,
  "analyser/smoothing": DefaultAnalyserParams.smoothingTimeConstant,
  "analyser/minDecibels": DefaultAnalyserParams.minDecibels,
  "analyser/maxDecibels": DefaultAnalyserParams.maxDecibels,
};

export interface WbaAnalyserActions {
  "analyser/enabled": { enabled: boolean };
  "analyser/config": Partial<Omit<WbaAnalyserState, "analyser/enabled">>;
}

export type WbaAnalyserMessage = Message<WbaAnalyserActions>;
type Handlers = ActionHandlers<WbaAnalyserActions, WbaAnalyserState>;
//#endregion

//#region utils
function Clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function FftSize(size: number) {
  return AnalyserFftSizes.reduce((best, n) =>
    Math.abs(Math.log2(n) - Math.log2(size)) < Math.abs(Math.log2(best) - Math.log2(size))
      ? n
      : best
  );
}

function Config(state: WbaAnalyserState, config: WbaAnalyserActions["analyser/config"]) {
  const next = { ...state, ...config };
  const minDb = Math.min(next["analyser/minDecibels"], next["analyser/maxDecibels"] - 1);
  return {
    ...next,
    "analyser/fps": Clamp(next["analyser/fps"], 1, 60),
    "analyser/fftSize": FftSize(next["analyser/fftSize"]),
    "analyser/smoothing": Clamp(next["analyser/smoothing"], 0, 1),
    "analyser/minDecibels": minDb,
  };
}
//#endregion

export interface WbaAnalyserProps {
  wbactx: WbaCtx;
  id?: string;
  initial?: Partial<WbaAnalyserState>;
  dispatcher?: DispatcherOptions;
}

export class WbaAnalyser
  extends Dispatcher<WbaAnalyserActions, WbaAnalyserState, WbaAnalyserProps> {
  protected readonly handlers: Handlers = {
    "analyser/enabled": ({ state, payload }) => ({ ...state, "analyser/enabled": payload.enabled }),
    "analyser/config": ({ state, payload }) => Config(state, payload),
  };

  #subs = new rxjs.Subscription();
  #node: AnalyserNode | null = null;
  #source: AudioNode | null = null;

  readonly analyse$: rxjs.Observable<AnalyseEvent>;
  readonly volume$: rxjs.Observable<number>;

  constructor(props: WbaAnalyserProps) {
    super(
      props,
      Config(WbaAnalyserDefaultState, props.initial ?? {}),
      props.dispatcher,
    );
    const { wbactx } = props;

    this.#subs.add(
      wbactx.select$((s) => s["ctx/status"]).subscribe((status) => {
        if (status === "uninit" || status === "closed") {
          this.#node = null;
          this.#source = null;
        }
      }),
    );
    this.#subs.add(this.select$((s) => s).subscribe(() => this.#apply()));

    this.analyse$ = this.select$((s) => s["analyser/fps"]).pipe(
      rxjs.switchMap((fps) => wbactx.tick$.pipe(rxjs.throttleTime(1000 / fps))),
      rxjs.filter(() =>
        this.state["analyser/enabled"] && !!this.#node && !!this.#source &&
        wbactx.state["ctx/status"] === "running"
      ),
      rxjs.map(() => this.read(this.#node!)),
      rxjs.share(),
    );
    this.volume$ = this.analyse$.pipe(
      rxjs.map((e) => e.volume),
      rxjs.share(),
    );
  }

  get wbactx(): WbaCtx {
    return this.props.wbactx;
  }

  get node(): AnalyserNode | null {
    return this.#node;
  }

  get source(): AudioNode | null {
    return this.#source;
  }

  connect(source: AudioNode | null) {
    if (source === this.#source) return;
    if (this.#source && this.#node) {
      try {
        this.#source.disconnect(this.#node);
      } catch (_e) {
        void _e;
      }
    }
    this.#source = source;
    if (!source) return;
    source.connect(this.#ensureNode());
  }

  read(node: AnalyserNode): AnalyseEvent {
    const timeDomain = new Float32Array(node.fftSize);
    const freqData = new Float32Array(node.frequencyBinCount);
    const byteFreq = new Uint8Array(node.frequencyBinCount);
    const byteTime = new Uint8Array(node.fftSize);
    node.getFloatTimeDomainData(timeDomain);
    node.getFloatFrequencyData(freqData);
    node.getByteFrequencyData(byteFreq);
    node.getByteTimeDomainData(byteTime);
    return { type: "analyser", timeDomain, freqData, byteFreq, byteTime, volume: Rms(timeDomain) };
  }

  #ensureNode(): AnalyserNode {
    if (this.#node) return this.#node;
    const id = this.props.id ?? "/analyser";
    this.#node = this.props.wbactx.node((c) => c.createAnalyser(), { id, name: id });
    this.#apply();
    return this.#node;
  }

  #apply() {
    const node = this.#node;
    if (!node) return;
    const s = this.state;
    node.fftSize = s["analyser/fftSize"];
    node.smoothingTimeConstant = s["analyser/smoothing"];
    node.minDecibels = s["analyser/minDecibels"];
    node.maxDecibels = s["analyser/maxDecibels"];
  }

  override dispose() {
    if (this.disposed) return;
    this.#subs.unsubscribe();
    this.connect(null);
    if (this.#node) this.props.wbactx.release(this.#node);
    this.#node = null;
    super.dispose();
  }
}
